import { ChevronLeft, ChevronRight } from "lucide-react";
import * as React from "react";
import { useCallback } from "react";
import {
	DayFlag,
	DayPicker,
	DayPickerProps,
	PropsSingle as DayPickerPropsSingle,
	SelectionState as CalendarSelectionState,
	UI as CalendarUI,
} from "react-day-picker";

import { cn } from "../../lib/utils";
import { buttonVariants } from "./button";

export type CalendarProps = Omit<DayPickerProps & DayPickerPropsSingle, "mode">;

function Calendar({ className, classNames, showOutsideDays = true, ...props }: CalendarProps) {
	const [month, setMonth] = React.useState(props.selected);

	const onDayClick = useCallback((day: Date, modifiers: any) => {
		if (modifiers.outside) {
			setMonth(day);
		}
	}, []);

	const onMonthChange = useCallback((newMonth: Date) => {
		setMonth(newMonth);
	}, []);

	return (
		<DayPicker
			month={month}
			onMonthChange={onMonthChange}
			showOutsideDays={showOutsideDays}
			className={cn("p-3", className)}
			classNames={{
				[CalendarUI.Month]: "space-y-4",
				[CalendarUI.MonthCaption]: "flex justify-center pt-1 z-0 relative items-center",
				[CalendarUI.CaptionLabel]: "text-sm font-medium",
				[CalendarUI.Nav]: "space-x-1 flex items-center",
				[CalendarUI.PreviousMonthButton]: cn(
					buttonVariants({ variant: "outline" }),
					"h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute top-3 left-3 z-10",
				),
				[CalendarUI.NextMonthButton]: cn(
					buttonVariants({ variant: "outline" }),
					"h-7 w-7 bg-transparent p-0 opacity-50 hover:opacity-100 absolute top-3 right-3 z-10",
				),
				[CalendarUI.MonthGrid]: "w-full border-collapse space-y-1",
				[CalendarUI.Weekdays]: "flex",
				[CalendarUI.Weekday]:
					"text-muted-foreground rounded-md w-9 font-normal text-[0.8rem]",
				[CalendarUI.Week]: "flex w-full mt-2",
				[CalendarUI.DayButton]: "w-full h-full",
				[CalendarUI.Day]: cn(
					buttonVariants({ variant: "ghost" }),
					"h-9 w-9 text-center font-normal text-sm p-0 relative [&:has([aria-selected].day-range-end)]:rounded-r-md [&:has([aria-selected].day-outside)]:bg-accent/50 [&:has([aria-selected])]:bg-accent first:[&:has([aria-selected])]:rounded-l-md last:[&:has([aria-selected])]:rounded-r-md focus-within:relative focus-within:z-20 aria-selected:opacity-100",
				),
				[CalendarSelectionState.range_end]: "day-range-end",
				[CalendarSelectionState.selected]:
					"bg-primary !text-primary-foreground hover:bg-primary hover:text-primary-foreground focus:bg-primary focus:text-primary-foreground",
				[DayFlag.outside]: "invisible",
				[DayFlag.disabled]:
					"text-muted-foreground/20 hover:bg-transparent hover:text-muted-foreground/20",
				[CalendarSelectionState.range_middle]:
					"aria-selected:bg-accent aria-selected:text-accent-foreground",
				[DayFlag.hidden]: "invisible",
				...classNames,
			}}
			components={{
				Chevron: (props) => {
					if (props.orientation === "left") {
						return <ChevronLeft className="h-4 w-4" {...props} />;
					}
					return <ChevronRight className="h-4 w-4" {...props} />;
				},
			}}
			{...props}
			onDayClick={onDayClick}
			mode="single"
		/>
	);
}
Calendar.displayName = "Calendar";

export { Calendar };
